/**
 * Types and normalization tables for pixel/tag fire intelligence: the
 * individual tracking calls we can see in a scan (declared in code) or on
 * the wire (runtime network capture), decoded into a comparable shape.
 */

import { ClientTypeType } from '../digital-audit-constants';
import { ECOM_TYPES } from './channel-health-constants';

export const FireSource = {
  /** Declared in page/JS source — fbq('track', …), gtag('event', …), dataLayer.push. */
  CODE: 'code',
  /**
   * Present in a fetched GTM container's own payload (Google's native
   * __googtag / __awct / __gaawe tag types) — the container is configured
   * to fire this, but GTM's own runtime dispatches it, so no literal call
   * appears anywhere in the page's HTML/JS. Weaker evidence than CODE (we
   * didn't see a specific call site) but far stronger than silence, since
   * these are the tag definitions GTM will actually execute.
   */
  CONTAINER: 'container',
  /** Observed as an outbound network request during a runtime scan. */
  WIRE: 'wire'
} as const;
export type FireSourceType = (typeof FireSource)[keyof typeof FireSource];

export interface TagFire {
  /** Display platform name, aligned with the detection signature names. */
  platform: string;
  source: FireSourceType;
  /** Destination id (measurement id, pixel id, advertiser id) when visible. */
  id: string;
  /** Platform-native event/call name (config, PageView, purchase, …). */
  event: string;
  /** Normalized cross-platform event, null when the event has no mapping. */
  canonicalEvent: string | null;
  /** Decoded parameters (shallow; values truncated for display). */
  params: Record<string, string>;
  /** How the fire is initiated, best-effort. */
  trigger: string;
  /** Where it was seen: evidence-blob source label or 'network'. */
  origin: string;
  /** Trimmed raw call / request for the drill-down view. */
  raw: string;
  // Which scan(s) produced this fire - set only by a full-scan merge (see
  // mergeTagFires in tag-fire-utils.ts); undefined for a report that has
  // never had a full scan merged in, since there's nothing to distinguish yet.
  seenIn?: ('quick' | 'full')[];
}

/** Canonical cross-platform events, in funnel order (matrix column order). */
export const CANONICAL_EVENTS = [
  'Page View',
  'View Content',
  'Search',
  'Add to Cart',
  'Begin Checkout',
  'Purchase',
  'Conversion',
  'Lead',
  'Signup',
  'Subscribe'
] as const;
export type CanonicalEvent = (typeof CANONICAL_EVENTS)[number];

/**
 * platform -> lowercased native event name -> canonical event.
 *
 * `'page hit'` is wireFire()'s fallback label (see tag-fire-utils.ts) when a
 * captured request matches a platform's WIRE_SIGNATURES pattern but none of
 * WIRE_EVENT_PARAMS's recognized keys identify the specific event - e.g.
 * Criteo encodes its event type inside per-request p0/p1/p2/p3 values this
 * generic decoder doesn't parse. For a platform whose base pixel snippet
 * fires unconditionally on every page load (the common case for ad-platform
 * pixels, as distinct from a conversion-only tag placed on one page type),
 * an unidentified hit is still that base beacon, so it canonicalizes as
 * 'Page View' the same way Klaviyo's 'active on site' already does for its
 * own differently-named equivalent. Deliberately absent from Google Ads
 * Conversion/Floodlight, which only ever fire on a conversion page - an
 * undecoded hit there is far more likely a conversion this decoder simply
 * couldn't classify than a page view, and mislabeling it would be worse
 * than leaving it unmapped.
 */
export const EVENT_CANON: Record<string, Record<string, CanonicalEvent>> = {
  'Google Analytics 4': {
    'page hit': 'Page View',
    page_view: 'Page View',
    view_item: 'View Content',
    view_item_list: 'View Content',
    search: 'Search',
    view_search_results: 'Search',
    add_to_cart: 'Add to Cart',
    begin_checkout: 'Begin Checkout',
    add_payment_info: 'Begin Checkout',
    purchase: 'Purchase',
    generate_lead: 'Lead',
    sign_up: 'Signup'
  },
  'Meta (Facebook) Pixel': {
    'page hit': 'Page View',
    pageview: 'Page View',
    viewcontent: 'View Content',
    search: 'Search',
    addtocart: 'Add to Cart',
    initiatecheckout: 'Begin Checkout',
    addpaymentinfo: 'Begin Checkout',
    purchase: 'Purchase',
    lead: 'Lead',
    completeregistration: 'Signup',
    subscribe: 'Subscribe'
  },
  'TikTok Pixel': {
    'page hit': 'Page View',
    pageview: 'Page View',
    page: 'Page View',
    viewcontent: 'View Content',
    search: 'Search',
    addtocart: 'Add to Cart',
    initiatecheckout: 'Begin Checkout',
    addpaymentinfo: 'Begin Checkout',
    completepayment: 'Purchase',
    placeanorder: 'Purchase',
    submitform: 'Lead',
    completeregistration: 'Signup',
    subscribe: 'Subscribe'
  },
  'Pinterest Tag': {
    'page hit': 'Page View',
    pagevisit: 'Page View',
    viewcategory: 'View Content',
    search: 'Search',
    addtocart: 'Add to Cart',
    checkout: 'Purchase',
    lead: 'Lead',
    signup: 'Signup'
  },
  'Snap Pixel': {
    'page hit': 'Page View',
    page_view: 'Page View',
    view_content: 'View Content',
    search: 'Search',
    add_cart: 'Add to Cart',
    start_checkout: 'Begin Checkout',
    purchase: 'Purchase',
    sign_up: 'Signup',
    subscribe: 'Subscribe'
  },
  'Microsoft UET': {
    'page hit': 'Page View',
    pageload: 'Page View',
    page_view: 'Page View',
    search: 'Search',
    add_to_cart: 'Add to Cart',
    begin_checkout: 'Begin Checkout',
    purchase: 'Purchase',
    submit_lead_form: 'Lead',
    generate_lead: 'Lead',
    sign_up: 'Signup',
    subscribe: 'Subscribe'
  },
  'Google Ads Conversion': {
    conversion: 'Conversion'
  },
  'Floodlight / DV360 / CM360': {
    conversion: 'Conversion'
  },
  'Reddit Pixel': {
    'page hit': 'Page View',
    pagevisit: 'Page View',
    viewcontent: 'View Content',
    search: 'Search',
    addtocart: 'Add to Cart',
    purchase: 'Purchase',
    lead: 'Lead',
    signup: 'Signup'
  },
  'X / Twitter Pixel': {
    'page hit': 'Page View',
    pageview: 'Page View',
    contentview: 'View Content',
    addtocart: 'Add to Cart',
    checkoutinitiated: 'Begin Checkout',
    purchase: 'Purchase'
  },
  'LinkedIn Insight': {
    'page hit': 'Page View',
    pageview: 'Page View',
    conversion: 'Conversion'
  },
  Klaviyo: {
    'page hit': 'Page View',
    'active on site': 'Page View',
    'viewed product': 'View Content',
    'added to cart': 'Add to Cart',
    'started checkout': 'Begin Checkout',
    'placed order': 'Purchase',
    'signed up': 'Signup',
    'subscribed to list': 'Subscribe'
  },
  // Criteo has no other decodable event name at all (its event type is
  // encoded inside per-request p0/p1/p2/p3 values, which this generic
  // decoder doesn't parse) - see the 'page hit' note above.
  Criteo: {
    'page hit': 'Page View'
  },
  'GTM dataLayer': {
    page_view: 'Page View',
    view_item: 'View Content',
    view_item_list: 'View Content',
    search: 'Search',
    add_to_cart: 'Add to Cart',
    begin_checkout: 'Begin Checkout',
    purchase: 'Purchase',
    generate_lead: 'Lead',
    sign_up: 'Signup'
  }
};

/**
 * Canonical parameter dimensions for the cross-platform comparison, mapped
 * from each platform's native parameter names (checked lowercased; wire
 * params like cd[value] included).
 */
export const PARAM_CANON: Record<string, string[]> = {
  value: [
    'value',
    'cd[value]',
    'revenue_value',
    'gv',
    'price',
    'epn.value',
    'revenue',
    'tr',
    '$value'
  ],
  currency: ['currency', 'cd[currency]', 'cu', 'currency_code', 'gc', 'cd[currency_code]'],
  items: [
    'items',
    'contents',
    'content_ids',
    'content_id',
    'cd[contents]',
    'cd[content_ids]',
    'line_items',
    'pr1',
    'ec',
    'el',
    'item_ids'
  ],
  'order id (dedup)': [
    'transaction_id',
    'ep.transaction_id',
    'order_id',
    'event_id',
    'eid',
    'cd[order_id]',
    'oid',
    'external_id'
  ],
  'identity (hashed)': ['em', 'ph', 'user_data', 'hashed_email', 'ud[em]', 'sha256_email', 'uid'],
  consent: ['gcs', 'gcd', 'npa', 'consent', 'dma', 'dma_cps'],
  // Lead-gen dimensions - first pass, refine against real lead-gen scan
  // evidence once this ships.
  'lead type': [
    'content_name',
    'content_category',
    'form_name',
    'form_id',
    'form_type',
    'lead_type'
  ],
  'lead qualification': [
    'lead_status',
    'lead_score',
    'predicted_ltv',
    'qualification',
    'mql',
    'sql'
  ]
};

/**
 * Which client types each PARAM_CANON dimension is worth showing a column
 * for - same absent-means-always-shown convention as KEY_EVENT_TYPES below.
 * Without this, a lead-gen scan's parameter-completeness table still shows
 * Value/Currency/Items/Order id columns that are eComm-only and always
 * empty for it.
 */
export const PARAM_DIM_TYPES: Partial<Record<string, ClientTypeType[]>> = {
  value: ECOM_TYPES as ClientTypeType[],
  currency: ECOM_TYPES as ClientTypeType[],
  items: ECOM_TYPES as ClientTypeType[],
  'order id (dedup)': ECOM_TYPES as ClientTypeType[],
  'lead type': ['leadgen', 'hybrid'],
  'lead qualification': ['leadgen', 'hybrid']
  // 'identity (hashed)'/'consent' intentionally absent - both apply to any
  // conversion event regardless of vertical.
};

/** Funnel-relevant events surfaced in Pixel Intel's parameter-completeness table. */
export const KEY_EVENTS: CanonicalEvent[] = [
  'Add to Cart',
  'Begin Checkout',
  'Purchase',
  'Conversion',
  'Lead',
  'Signup',
  'Subscribe'
];

/**
 * Which client types each key event is worth surfacing for - an absent
 * entry means "always shown" (e.g. Conversion, a vertical-neutral generic
 * goal signal). Mirrors ChannelCheck.types/ECOM_TYPES in
 * channel-health-constants.ts.
 */
export const KEY_EVENT_TYPES: Partial<Record<CanonicalEvent, ClientTypeType[]>> = {
  'Add to Cart': ECOM_TYPES as ClientTypeType[],
  'Begin Checkout': ECOM_TYPES as ClientTypeType[],
  Purchase: ECOM_TYPES as ClientTypeType[],
  Lead: ['leadgen', 'hybrid'],
  Signup: ['leadgen', 'hybrid', 'ecomm_sub'],
  Subscribe: ['leadgen', 'hybrid', 'ecomm_sub']
};

/**
 * The event(s) that represent "the money/lead signal fired cleanly" per
 * client type - drives buildFireDifferences' revenue/lead-completeness and
 * dedup-readiness rules instead of hardcoding 'Purchase'. Hybrid checks
 * both signals independently, so it can surface two rows for the same
 * underlying check - that's expected, not a bug.
 */
export const PRIMARY_CONVERSION_EVENTS: Record<ClientTypeType, CanonicalEvent[]> = {
  ecomm: ['Purchase'],
  ecomm_sub: ['Purchase'],
  ecomm_store: ['Purchase'],
  leadgen: ['Lead'],
  hybrid: ['Purchase', 'Lead']
};

/** Wire endpoints -> platform. Mirrors runtime_scanner.py's fire signatures. */
export const WIRE_SIGNATURES: [RegExp, string][] = [
  [/google-analytics\.com\/g\/collect|analytics\.google\.com\/g\/collect/i, 'Google Analytics 4'],
  [
    /googleadservices\.com\/pagead\/conversion|google\.com\/pagead\/1p-conversion/i,
    'Google Ads Conversion'
  ],
  [/fls\.doubleclick\.net|ad\.doubleclick\.net\/ddm/i, 'Floodlight / DV360 / CM360'],
  [/facebook\.com\/tr[/?]/i, 'Meta (Facebook) Pixel'],
  [/analytics\.tiktok\.com/i, 'TikTok Pixel'],
  [/ct\.pinterest\.com/i, 'Pinterest Tag'],
  [/tr\.snapchat\.com/i, 'Snap Pixel'],
  [/px\.ads\.linkedin\.com|linkedin\.com\/li\/track/i, 'LinkedIn Insight'],
  [/bat\.bing\.com\/action/i, 'Microsoft UET'],
  [/analytics\.twitter\.com|t\.co\/i\/adsct/i, 'X / Twitter Pixel'],
  [/alb\.reddit\.com|events\.redditmedia\.com/i, 'Reddit Pixel'],
  [/widget\.criteo\.com|sslwidget\.criteo\.com/i, 'Criteo'],
  [/a\.klaviyo\.com|static\.klaviyo\.com\/onsite/i, 'Klaviyo']
];

/** Wire query params that identify the destination account/pixel. */
export const WIRE_ID_PARAMS = ['tid', 'id', 'pixel_id', 'sdkid', 'pid', 'ti', 'txn_id', 'src'];

/** Wire query params that carry the event name. */
export const WIRE_EVENT_PARAMS = ['en', 'ev', 'event', 'evt', 'type', 'ea'];
